
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { removeSessionInfo, collapseIframe, expandIframe } from '../../../helpers/helpers';
import {
  Card,
  CloseButton,
  IconArrowOpenUpLine,
  IconButton,
  IconArrowOpenDownLine,
  Flex,
  Text,
} from '@smarterservices/smarter-ui';
import { useAppStore } from '../../../stores/app';

const useWindowHeight = () => {
  const [windowHeight, setWindowHeight] = useState(0);

  useLayoutEffect(() => {
    // Function to update the height state
    const updateHeight = () => {
      setWindowHeight(window.innerHeight);
    };

    // Set initial height
    updateHeight();

    // Add event listener for window resize
    window.addEventListener('resize', updateHeight);

    // Clean up event listener on unmount
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return windowHeight;
};

const SessionInfo = () => {
  const [token, setToken] = useState(null);  
  const [sessionSid, setSessionSid] = useState(null);
  const [examSid, setExamSid] = useState(null);
  const [installSid, setInstallSid] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sessionStart, setSessionStart] = useState(null);
  const windowHeight = useWindowHeight();
  const { getSessionStartData } = useAppStore();
  // watch for the initialized state in the app store
  const appInitialized = useAppStore(
    (state) => state.initialized
  );
  const initialQueryParams = useAppStore(
    (state) => state.initialQueryParams
  );

  useEffect(() => {
    const initSessionInfo = async () => {

      if(appInitialized){

       const storeState = useAppStore.getState();
       
        // Validate required session data exists
        if (!storeState.token || !storeState.sessionSid || !storeState.examSid || !storeState.installSid) {
          console.error('Missing required session data in store');
          return;
        }

        setToken(storeState.token);
        setSessionSid(storeState.sessionSid);
        setExamSid(storeState.examSid);
        setInstallSid(storeState.installSid);    
        
        // TODO: here we can call the API to get any additional info needed for enrichment (stepData, etc)
        const sessionStartData = await getSessionStartData();
        setSessionStart(sessionStartData);

        // Set expanded state and collapse iframe if needed
        const isExpanded = initialQueryParams?.isExpanded === 'true' ?? true;
        setIsExpanded(isExpanded);

        if (!isExpanded) {
          collapseIframe('spSessionInfoIframeId');
        }
      }

    };
    initSessionInfo();
  }, [appInitialized]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      collapseIframe('spSessionInfoIframeId');
    } else {
      expandIframe('spSessionInfoIframeId');
    }
  };

  const toHumanReadable = (camelCase) => {
    // Convert camelCase to Title Case with spaces
    return (
      camelCase
        // Add space before capital letters
        .replace(/([A-Z])/g, ' $1')
        // Capitalize first letter
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    );
  };

  const HEADER_HEIGHT = 50;
  const bodyHeight =
    windowHeight > 0 ? `${windowHeight - HEADER_HEIGHT}px` : '100vh';

  return sessionSid && examSid && installSid && token && sessionStart ? (
    <Card
      background="secondary"
      borderColor="brand"
      borderWidth="large"
      borderRadius="x-small"
    >
      <Card.Header background="brand">
        <Card.Title>Session Info</Card.Title>
        <Card.Actions>
          {isExpanded ? (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Collapse"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenUpLine />
            </IconButton>
          ) : (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Expand"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenDownLine />
            </IconButton>
          )}
          <CloseButton
            color="primary-inverse"
            screenReaderLabel="Close"
            onClick={removeSessionInfo}
          />
        </Card.Actions>
      </Card.Header>
      {isExpanded && (
        <Card.Body padding="medium" height={bodyHeight} overflowY="auto">
          <Flex direction="column">
            <Text as="h3" size="medium">
              Course:
            </Text>
            <Flex margin="0 0 medium 0">
              <Text>{sessionStart.courseTitle}</Text>
            </Flex>

            <Text as="h3" size="medium">
              Exam:
            </Text>
            <Flex margin="0 0 medium 0">
              <Text>{sessionStart.examTitle}</Text>
            </Flex>

            <Text as="h3" size="medium">
              Duration:
            </Text>
            <Flex margin="0 0 medium 0">
              <Text>
                {sessionStart.examDuration} minutes
              </Text>
            </Flex>

            {sessionStart.permittedItems &&
              Object.entries(sessionStart.permittedItems).length > 0 && (
                <>
                  <Text as="h3" size="medium">
                    Permitted Items:
                  </Text>
                  <Flex margin="0 0 medium 0">
                    <ul className="permitted-items">
                        {Object.entries(sessionStart.permittedItems).map(
                          ([key, value]) => (
                            <li key={key}>
                              <Text>
                                {toHumanReadable(key)}
                                {value.notes && ':'}
                                {value.notes && (
                                  <span className="permitted-item-notes">
                                    {value.notes}
                                  </span>
                                )}
                              </Text>
                            </li>
                          )
                        )}
                      </ul>
                    </Flex>
                  </>
                )}

              {sessionStart.studentNotes && (
                <>
                  <Text as="h3" size="medium">
                    Student Notes:
                  </Text>
                  <div className="student-notes">
                    <Flex margin="0 0 medium 0">
                      <Text>{sessionStart.studentNotes}</Text>
                    </Flex>
                  </div>
                </>
              )}
            </Flex>
        </Card.Body>
      )}
    </Card>
  ) : null;
};

export default SessionInfo;